<script setup lang="ts">
import { XIcon } from '@clicksign/excalibur';
import { useToast } from 'vue-toastification';
import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { useQueryClient } from '@tanstack/vue-query';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';

import { urlToBlob, trackElapsedTime, Decisions, AppFeature, useFeature, isFetchError } from '~~core';
import { BrandButton } from '~~base-ui';
import { useRequirement } from '~~requirement';

import { useFetchSigner } from '~~/domains/requirement/repositories/signer';
import { PhotoObjectType } from '~~/domains/requirement/components/DocumentDetectorCapture/DocumentDetectorCapture.vue';
import DocumentDetectorCaptureOld from '~~/domains/requirement/components/DocumentDetectorCaptureOld/DocumentDetectorCaptureOld.vue';
import { Provider } from '~~/domains/requirement/constants/caf';
import { checkOcr } from '~~/domains/requirement/services/facial-biometrics';

import SkeletonGenericRequirement from '../../../../components/SkeletonGenericRequirement/SkeletonGenericRequirement.vue';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { useFetchRequirement } from '../../../../repositories/requirement';
import {
  LivenessCameraModal,
  LivenessCarousel,
  FacialBiometricsHelpModal,
  DocumentSelector,
  DocumentDetectorCapture,
  FacialBiometricsFacematchStatus,
} from '../../../../components';
import { buildCarouselItems, DocumentObjType, DOCUMENTS_TYPE } from '../../../../constants/facialBiometrics';
import { isCameraConsented } from '../../../../composables/use-document-detector-camera';
import { useFetchCAFCredentials } from '../../../../repositories/caf';
import { useFetchFaceMatchesConfig } from '../../../../repositories/facial-biometrics';
import { appendFacematchPhoto, fetchFacematchInfo, fetchFacematchStatus, saveRequirement } from '../../../../services';
import { DocumentErrors, FacematchInfo } from '../../../../schemas/facial-biometrics';

type SavedFacematchData = {
  facematchKey: string | null,
  signatureRequestKey: string | null
  type: string | null
} | null;

const STEP_NAME = 'facial_biometrics';

const { signatureRequestKey, finishRequirement, setActiveRequirement } = useRequirementStep();

setActiveRequirement(STEP_NAME);

const { requirement, isLoading: isQueryLoading, suspense } = useFetchRequirement('facial_biometrics', signatureRequestKey.value, { staleTime: minutesToMilliseconds(20) });
const { remainingAttempts } = useRequirement(requirement);
const { faceMatchesConfig, isLoading: isFaceMatchesConfigLoading } = useFetchFaceMatchesConfig(signatureRequestKey);
const { credentials } = useFetchCAFCredentials(signatureRequestKey);
const { signer } = useFetchSigner(signatureRequestKey);

const toast = useToast();
const gtm = useGtm();
const { getTimeElapsed } = trackElapsedTime();
const { t } = useI18n();
const carouselItems = buildCarouselItems(t);

const title = t('requirement.facialBiometrics.title');

useHead({ title });

let savedFacematch: FacematchInfo = reactive({
  key: '',
  status: '',
  documentType: '',
  underReview: false,
  evidences: [],
});

const restartFacematch: Ref<boolean> = ref(false);

const capturedDocuments: DocumentObjType = reactive({
  front: undefined,
  back: undefined,
});

const capturedSelfie: Ref<Blob | undefined> = ref();

const pageControlData = reactive({
  carouselCurrentStep: 0,
  showHelpModal: false,
  isLoading: false,
  currentOverlay: '',
  currentStep: 'carousel',
});

const isCarouselLastStep = computed(
  () => pageControlData.carouselCurrentStep >= carouselItems.length - 1,
);

const currentProvider = ref();

const isNextCode = computed(() => currentProvider.value === Provider.NEXT_CODE);

const advanceCarouselStep = () => {
  if (!isCarouselLastStep.value) {
    pageControlData.carouselCurrentStep += 1;
  }

  if (isCarouselLastStep.value) {
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_name: 'FacialBiometricsStart',
      ga_event_label: isNextCode.value ? 'nextCode' : 'caf',
      ga_options: isNextCode.value ? 'nextCode' : 'caf',
    });
  }
};

const backCarouselStep = () => {
  if (pageControlData.carouselCurrentStep > 0) {
    pageControlData.carouselCurrentStep -= 1;
  }
};

const showCameraError = (error: string) => toast.error(t(`requirement.facialBiometrics.error.camera.${error}`));

const handleCarouselButton = async () => {
  pageControlData.isLoading = true;

  if (isCarouselLastStep.value) {
    const { isConsented, erroMenssage } = await isCameraConsented();

    if (isConsented) pageControlData.currentStep = 'documentType';
    if (erroMenssage) showCameraError(erroMenssage);
  }
  advanceCarouselStep();
  pageControlData.isLoading = false;
};

const closeOverlay = () => {
  pageControlData.isLoading = false;
  pageControlData.currentStep = 'documentType';
  pageControlData.currentOverlay = '';
};

// TODO: check if limit is exceeded
const { isEnabled: isFacematchRetriesLimitEnabled } = useFeature(Decisions.TOGGLE_FACEMATCH_RETRIES_LIMIT);

const useRedirectToFeedback = () => {
  const route = useRoute();
  const localePath = useLocalePath();
  const localRemainingAttempts = ref(0);

  function checkAndgoToFeedback() {
    // TOGGLE_FACEMATCH_RETRIES_LIMIT
    // TODO: when toggle is removed remove isFacematchRetriesLimitEnabled the check
    if (localRemainingAttempts.value <= 0 && isFacematchRetriesLimitEnabled.value) {
      return navigateTo(localePath({
        name: 'signatureRequestKind-signatureRequestKey-feedback',
        params: route.params,
        query: { ...route.query, status: 'facial_biometrics_limit_exceeded' },
      }));
    }
  }

  const queryClient = useQueryClient();

  async function handleCaptureFailed() {
    localRemainingAttempts.value -= 1;

    if (localRemainingAttempts.value <= 0) {
      // TODO: integrate
      // await rejected(signatureRequestKey.value);
      queryClient.cancelQueries();
    }

    await checkAndgoToFeedback();
  }

  watch(remainingAttempts, () => {
    if (!Number.isInteger(remainingAttempts.value)) return;

    localRemainingAttempts.value ||= remainingAttempts.value || 0;

    checkAndgoToFeedback();
  }, { immediate: true });

  return { handleCaptureFailed, localRemainingAttempts };
};

const { handleCaptureFailed, localRemainingAttempts } = useRedirectToFeedback();

const resetFacialBiometry = async (initialStep = 'carousel', hardReset?: boolean) => {
  pageControlData.carouselCurrentStep = 0;
  pageControlData.currentStep = initialStep;
  pageControlData.currentOverlay = '';
  pageControlData.isLoading = false;
  capturedDocuments.front = undefined;
  capturedDocuments.back = undefined;
  capturedSelfie.value = undefined;
  if (hardReset) restartFacematch.value = hardReset;
};

const handleFacematchTryAgain = () => {
  resetFacialBiometry('documentType', true);
};

onMounted(async () => {
  await suspense();
  const hasEvidence = requirement.value?.step?.evidences.some((evidence) => evidence.state === 'done' || evidence.state === 'pending');

  if (hasEvidence) finishRequirement();
  await resetFacialBiometry();
});

const nextStepButtonText = computed(() => (pageControlData.currentStep === 'carousel' && isCarouselLastStep.value
  ? t('requirement.facialBiometrics.allowCamera')
  : t('common.next')));

const isCurrentStep = (step: string) => pageControlData.currentStep === step;

const isCurrentOverlay = (step: string) => pageControlData.currentOverlay === step;

const handleFinishDocumentCapture = async (document: DocumentObjType) => {
  capturedDocuments.front = document.front;
  capturedDocuments.back = document.back;
  pageControlData.currentOverlay = 'liveness';
};

const handleErrorDocumentCapture = (error: string) => {
  pageControlData.carouselCurrentStep = 2;
  pageControlData.currentStep = 'carousel';
  pageControlData.isLoading = false;
  pageControlData.currentOverlay = '';

  showCameraError(error);
};

const handleCloseDocumentCapture = () => closeOverlay();

async function fetchFacematchData(type: string) {
  const savedFacematchData = useLocalStorage<SavedFacematchData | null>('facematchData', null, { serializer: StorageSerializers.object });

  if (type !== savedFacematchData.value?.type) {
    savedFacematchData.value = null;
  }

  const isSameRequestKey = savedFacematchData?.value?.signatureRequestKey === signatureRequestKey.value;

  if (savedFacematchData?.value?.facematchKey && isSameRequestKey && !restartFacematch.value) {
    savedFacematch = await fetchFacematchInfo(signatureRequestKey.value, type, savedFacematchData.value?.facematchKey);
  } else {
    savedFacematch = await fetchFacematchInfo(signatureRequestKey.value, type);
  }
  const facematchDataObject = {
    facematchKey: savedFacematch.key,
    signatureRequestKey: signatureRequestKey.value,
    type,
  };

  currentProvider.value = savedFacematch.provider;

  restartFacematch.value = false;
  savedFacematchData.value = facematchDataObject;
}

const handleSelectDocumentType = async (type: string) => {
  pageControlData.isLoading = true;
  await fetchFacematchData(type);
  const mappedEvidences = savedFacematch.evidences.map(async (evidence) => {
    if (evidence?.kind === DOCUMENTS_TYPE.front) capturedDocuments.front = await urlToBlob(evidence.url);
    if (evidence?.kind === DOCUMENTS_TYPE.back) capturedDocuments.back = await urlToBlob(evidence.url);
    if (evidence?.kind === DOCUMENTS_TYPE.selfie) capturedSelfie.value = await urlToBlob(evidence.url);

    return evidence;
  });

  await Promise.all(mappedEvidences);

  if (type !== savedFacematch.documentType) {
    capturedDocuments.front = undefined;
    capturedDocuments.back = undefined;
    savedFacematch.documentType = type;
  }
  pageControlData.currentOverlay = 'documentCapture';
};

const checkFacematchStatus = async () => {
  let statusCheck = '';
  let attempts = 1;
  const interval = secondsToMilliseconds(faceMatchesConfig.value?.asyncChecking.intervalInSeconds);
  const maxAttempts = faceMatchesConfig.value.asyncChecking.quantity;

  while (attempts < maxAttempts) {
    const { status } = (await fetchFacematchInfo(signatureRequestKey.value, savedFacematch.documentType, savedFacematch.key)); // eslint-disable-line no-await-in-loop

    statusCheck = status;

    if (statusCheck !== 'pending') {
      break;
    } else {
      await new Promise((resolve) => { // eslint-disable-line no-await-in-loop
        setTimeout(resolve, interval);
      });
    }
    attempts += 1;
  }

  return statusCheck;
};

const picture = ref();

const startFacematch = async (image?: Blob) => {
  pageControlData.currentOverlay = 'facematch';
  await fetchFacematchStatus(signatureRequestKey.value, savedFacematch.key);
  await saveRequirement('facial_biometrics', signatureRequestKey.value, { facematchKey: savedFacematch.key });
  const status = await checkFacematchStatus();

  pageControlData.isLoading = false;

  if (!['approved', 'pending'].includes(status)) {
    // TOGGLE_FACEMATCH_RETRIES_LIMIT
    // TODO: when toggle is removed remove isFacematchRetriesLimitEnabled the check
    if (isFacematchRetriesLimitEnabled.value) {
      picture.value = image;
      await handleCaptureFailed();
    }

    return;
  }

  gtm?.trackEvent({
    event: 'ga-send',
    ga_event_name: 'FacialBiometricsTimestamp',
    ga_event_category: isNextCode.value ? 'NextCodeTimestamp' : 'CafTimestamp',
    ga_event_label: getTimeElapsed(),
    ga_options: getTimeElapsed(),
    ga_event_action: 'timing',
  });

  pageControlData.currentOverlay = '';
  useLocalStorage<SavedFacematchData | null>('facematchData', null).value = null;
  finishRequirement();
};

const sendDocumentPhoto = async (image: Blob | undefined, position: string) => {
  if (image) {
    await appendFacematchPhoto(
      signatureRequestKey.value,
      savedFacematch.key,
      image,
      'document',
      position,
    );
  }
};

const captureKey = ref(0);

const handleReset = () => {
  captureKey.value += 1;
  capturedDocuments.front = undefined;
  capturedDocuments.back = undefined;
};

const { isEnabled: isOcrSyncEnabled } = useFeature(Decisions.OCR_SYNC);

// OCR_SYNC
// TODO: when toggle is removed remove the isOcrSyncEnabled check
function checkFetchError(error: Error) {
  if (isOcrSyncEnabled.value && isFetchError(error)) {
    handleReset();
    showCameraError('unknownCameraError');
  }
}

const handleSendPhoto = async ({ image, side }: PhotoObjectType) => {
  await sendDocumentPhoto(image, side);

  // OCR_SYNC
  // TODO: when toggle is removed remove the isOcrSyncEnabled check
  if (!isOcrSyncEnabled.value || !signer.value?.documentation || side !== 'back') return;

  let data;

  try {
    data = await checkOcr(signatureRequestKey.value, 'facematch', savedFacematch.key, signer.value.documentation);
  } catch (err: any) {
    checkFetchError(err);
  }

  if (data?.documentationMatch === 'expired') throw new Error(DocumentErrors.DocumentExpired);
  if (data?.documentationMatch !== 'success') throw new Error(DocumentErrors.CpfError);
};

// TOGGLE_NEXT_CODE_IMPLEMENTATION
// TODO: remove this feature flag when the feature is enabled for all users
const { isEnabled } = useFeature(Decisions.TOGGLE_NEXT_CODE_IMPLEMENTATION);

// TOGGLE_NEXT_CODE_IMPLEMENTATION
// TODO: remove this computed when the feature is enabled for all users
const canShowNextCode = computed(() => isEnabled.value);

const disabledDocuments = useState('disabledDocuments', () => new Set<string>());
</script>

<template>
  <div>
    <SkeletonGenericRequirement v-if="!credentials || !signer?.documentation || isQueryLoading || isFaceMatchesConfigLoading" />

    <section
      v-else
      :class="$style.facialBiometricsContainer"
      @keyup.enter.stop
    >
      <FacialBiometricsHelpModal
        v-if="pageControlData.showHelpModal"
        @close="pageControlData.showHelpModal = false"
      />

      <template v-if="isCurrentStep('carousel')">
        <LivenessCarousel
          :items="carouselItems"
          :selected="pageControlData.carouselCurrentStep"
          @back="backCarouselStep"
          @next="advanceCarouselStep"
        />

        <div :class="$style.stepControl">
          <BrandButton
            full-width
            :loading="pageControlData.isLoading"
            @click="handleCarouselButton"
          >
            {{ nextStepButtonText }}
            <XIcon
              right
              icon="arrow-right"
            />
          </BrandButton>

          <BrandButton
            design="link"
            @click="pageControlData.showHelpModal = true"
          >
            {{ $t('requirement.liveness.knowMore') }}
          </BrandButton>
        </div>
      </template>

      <DocumentSelector
        v-if="isCurrentStep('documentType')"
        :value="savedFacematch.documentType"
        :loading="pageControlData.isLoading"
        :disabled-documents="disabledDocuments"
        @selected="handleSelectDocumentType"
      />
      <!-- TOGGLE_NEXT_CODE_IMPLEMENTATION
      TODO: Keep only default slot when remove the toggle -->
      <AppFeature :name="Decisions.TOGGLE_NEXT_CODE_IMPLEMENTATION">
        <DocumentDetectorCapture
          v-if="isCurrentOverlay('documentCapture')"
          :key="captureKey"
          :document-type="savedFacematch.documentType"
          :documents="capturedDocuments"
          :documentation="signer.documentation"
          :facematch-key="savedFacematch.key"
          :signature-request-key="signatureRequestKey"
          :token="credentials.token"
          :environment="credentials.environment"
          :provider="currentProvider"
          :send-photo-action="handleSendPhoto"
          @reset="handleReset"
          @update:disabled-documents="disabledDocuments = new Set([...disabledDocuments, $event])"
          @finish-capture="handleFinishDocumentCapture"
          @error-capture="handleErrorDocumentCapture"
          @close="handleCloseDocumentCapture"
        />
        <template #off>
          <DocumentDetectorCaptureOld
            v-if="isCurrentOverlay('documentCapture')"
            :document-type="savedFacematch.documentType"
            :documents="capturedDocuments"
            :documentation="signer.documentation"
            :facematch-key="savedFacematch.key"
            :signature-request-key="signatureRequestKey"
            :token="credentials.token"
            :environment="credentials.environment"
            @finish-capture="handleFinishDocumentCapture"
            @error-capture="handleErrorDocumentCapture"
            @close="handleCloseDocumentCapture"
          />
        </template>
      </AppFeature>
      <div
        v-if="isCurrentOverlay('liveness')"
        :class="$style.liveness"
      >
        <LivenessCameraModal
          :person-id="signer.documentation"
          :token="credentials.token"
          :environment="credentials.environment"
          :signature-request-key="signatureRequestKey"
          :facematch-key="savedFacematch.key"
          :liveness-evidence="capturedSelfie"
          :requirement="'liveness'"
          :provider="currentProvider"
          :can-show-next-code="canShowNextCode"
          @close="closeOverlay"
          @liveness-finished="startFacematch"
        />
      </div>
      <FacialBiometricsFacematchStatus
        v-if="isCurrentOverlay('facematch')"
        :loading="pageControlData.isLoading"
        :picture="picture"
        :remaining-attempts="localRemainingAttempts"
        @try-again="handleFacematchTryAgain"
      />
    </section>
  </div>
</template>

<style lang="scss" module>
.facialBiometricsContainer {
  display: flex;
  align-items: center;
  flex-direction: column;

  .stepControl {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 492px;
    padding: var(--space-medium) var(--space-regular);
    gap: var(--space-regular);
  }
}

.liveness {
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
}
</style>
